import { useInfiniteQuery, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/shared/api';
import {
  createKmEntityType,
  deleteKmEntityType,
  fetchKmAttrTypes,
  fetchKmDomains,
  fetchKmEntities,
  fetchKmEntity,
  fetchKmEntityGraph,
  fetchKmEntityType,
  fetchKmEntityTypeAttrs,
  fetchKmEntityTypes,
  fetchKmRelationTypes,
  searchKmEntities,
  updateKmEntity,
  updateKmEntityType,
} from './api';
import type {
  KmAttrTypesParams,
  KmDomainsParams,
  KmEntitiesParams,
  KmEntityGraphParams,
  KmEntitySearch,
  KmEntityTypesParams,
  KmPage,
  KmRelationTypesParams,
} from '../types';

const KM_ROOT = 'km';
const ENTITY_TYPES = 'entity-types';
const ENTITIES = 'entities';

/** Размер окна списка и поиска экземпляров по умолчанию */
const ENTITIES_PAGE_SIZE = 50;

/**
 * Ключи кэша TanStack Query для модели знаний.
 */
export const kmKeys = {
  all: [KM_ROOT] as const,
  entityTypeLists: () => [KM_ROOT, ENTITY_TYPES, 'list'] as const,
  entityTypeList: (params?: KmEntityTypesParams) => [KM_ROOT, ENTITY_TYPES, 'list', { ...params }] as const,
  entityTypeDetail: (code?: string) => [KM_ROOT, ENTITY_TYPES, 'detail', code] as const,
  entityTypeAttrs: (code?: string) => [KM_ROOT, ENTITY_TYPES, 'attrs', code] as const,
  entityLists: () => [KM_ROOT, ENTITIES, 'list'] as const,
  entityList: (params?: KmEntitiesParams) => [KM_ROOT, ENTITIES, 'list', { ...params }] as const,
  // Догрузка окнами держит в кэше данные другой формы, чем разовый запрос списка, — свой сегмент ключа
  entityInfiniteLists: () => [KM_ROOT, ENTITIES, 'infinite'] as const,
  entityInfiniteList: (params?: KmEntitiesParams) => [KM_ROOT, ENTITIES, 'infinite', { ...params }] as const,
  entitySearches: () => [KM_ROOT, ENTITIES, 'search'] as const,
  entitySearch: (params: KmEntitySearch) => [KM_ROOT, ENTITIES, 'search', { ...params }] as const,
  // Разовый поиск держит в кэше одно окно выдачи, догрузка окнами — их набор: сегменты разные
  entitySearchPage: (params: KmEntitySearch) => [KM_ROOT, ENTITIES, 'search-page', { ...params }] as const,
  entityDetails: () => [KM_ROOT, ENTITIES, 'detail'] as const,
  entityDetail: (id: number) => [KM_ROOT, ENTITIES, 'detail', id] as const,
  entityGraphs: () => [KM_ROOT, ENTITIES, 'graph'] as const,
  entityGraph: (id: number, params?: KmEntityGraphParams) => [KM_ROOT, ENTITIES, 'graph', id, { ...params }] as const,
  attrTypeList: (params?: KmAttrTypesParams) => [KM_ROOT, 'attr-types', 'list', { ...params }] as const,
  domainList: (params?: KmDomainsParams) => [KM_ROOT, 'domains', 'list', { ...params }] as const,
  relationTypeList: (params?: KmRelationTypesParams) => [KM_ROOT, 'relation-types', 'list', { ...params }] as const,
};

// Запрос активен только для реального кода типа (не в режиме создания)
const isRealCode = (code?: string) => !!code && code !== 'create';

// Следующее окно: его нет, когда пришло меньше запрошенного либо выбран весь total
const getNextOffset = (page: KmPage<unknown>, loadedCount: number, pageSize: number) => {
  if (page.items.length < pageSize || loadedCount >= page.total) {
    return undefined;
  }
  return loadedCount;
};

/**
 * Хук списка типов сущностей.
 */
export const useKmEntityTypes = (params?: KmEntityTypesParams) =>
  useQuery({
    queryKey: kmKeys.entityTypeList(params),
    queryFn: () => fetchKmEntityTypes(params),
  });

/**
 * Хук карточки типа сущности по коду.
 */
export const useKmEntityType = (code?: string) =>
  useQuery({
    queryKey: kmKeys.entityTypeDetail(code),
    queryFn: () => fetchKmEntityType(code!),
    enabled: isRealCode(code),
  });

/**
 * Хук схемы типа (привязок атрибутов) по коду типа.
 */
export const useKmEntityTypeAttrs = (code?: string) =>
  useQuery({
    queryKey: kmKeys.entityTypeAttrs(code),
    queryFn: () => fetchKmEntityTypeAttrs(code!),
    enabled: isRealCode(code),
  });

/**
 * Хук списка экземпляров модели знаний. `enabled` отключает список там, где выдачу
 * даёт другой источник (полнотекстовый поиск, знание прежней базы).
 */
export const useKmEntities = (params?: KmEntitiesParams, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: kmKeys.entityList(params),
    queryFn: () => fetchKmEntities(params),
    enabled: options?.enabled ?? true,
  });

/**
 * Хук списка экземпляров с догрузкой окнами: `limit` задаёт размер окна,
 * смещение ведёт сама догрузка. `enabled` отключает список там, где выдачу
 * даёт другой источник (полнотекстовый поиск).
 */
export const useKmEntitiesInfinite = (params?: KmEntitiesParams, options?: { enabled?: boolean }) => {
  const pageSize = params?.limit ?? ENTITIES_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: kmKeys.entityInfiniteList(params),
    queryFn: ({ pageParam }) => fetchKmEntities({ ...params, limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    enabled: options?.enabled ?? true,
    getNextPageParam: (lastPage, allPages) =>
      getNextOffset(
        lastPage,
        allPages.reduce((count, page) => count + page.items.length, 0),
        pageSize,
      ),
  });
};

/**
 * Хук карточки экземпляра по идентификатору.
 */
export const useKmEntity = (id?: number) =>
  useQuery({
    queryKey: id ? kmKeys.entityDetail(id) : kmKeys.entityDetails(),
    queryFn: () => fetchKmEntity(id!),
    enabled: !!id,
  });

/**
 * Хук карточек экземпляров по набору идентификаторов. Ключи — те же, что и у одиночной
 * карточки, поэтому уже прочитанный экземпляр берётся из кэша, а не запрашивается заново.
 */
export const useKmEntitiesByIds = (ids: number[]) =>
  useQueries({
    queries: ids.map((id) => ({
      queryKey: kmKeys.entityDetail(id),
      queryFn: () => fetchKmEntity(id),
    })),
    combine: (results) => results.map((result) => result.data).filter((entity) => entity !== undefined),
  });

/**
 * Хук полнотекстового поиска экземпляров по значениям атрибутов — с догрузкой окнами,
 * как и обычный список. Активен только при непустой строке поиска.
 */
export const useSearchKmEntitiesInfinite = (params: KmEntitySearch) => {
  const pageSize = params.limit ?? ENTITIES_PAGE_SIZE;

  return useInfiniteQuery({
    queryKey: kmKeys.entitySearch(params),
    queryFn: ({ pageParam }) => searchKmEntities({ ...params, limit: pageSize, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      getNextOffset(
        lastPage,
        allPages.reduce((count, page) => count + page.items.length, 0),
        pageSize,
      ),
    enabled: Boolean(params.query),
  });
};

/**
 * Хук полнотекстового поиска экземпляров одним окном выдачи — для мест, где нужны только
 * первые совпадения (список подсказок), а догрузка не предполагается.
 * Активен только при непустой строке поиска.
 */
export const useSearchKmEntities = (params: KmEntitySearch, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: kmKeys.entitySearchPage(params),
    queryFn: () => searchKmEntities(params),
    enabled: Boolean(params.query) && (options?.enabled ?? true),
  });

/**
 * Хук графа связей экземпляра: связи вместе со вторыми сторонами.
 */
export const useKmEntityGraph = (id?: number, params?: KmEntityGraphParams) =>
  useQuery({
    queryKey: id ? kmKeys.entityGraph(id, params) : kmKeys.entityGraphs(),
    queryFn: () => fetchKmEntityGraph(id!, params),
    enabled: !!id,
  });

/**
 * Хук справочника типов атрибутов (имя и тип данных для схемы).
 */
export const useKmAttrTypes = (params?: KmAttrTypesParams) =>
  useQuery({
    queryKey: kmKeys.attrTypeList(params),
    queryFn: () => fetchKmAttrTypes(params),
  });

/**
 * Хук справочника доменов — источник названий групп типов.
 */
export const useKmDomains = (params?: KmDomainsParams) =>
  useQuery({
    queryKey: kmKeys.domainList(params),
    queryFn: () => fetchKmDomains(params),
  });

/**
 * Хук списка типов связей. Без фильтров отдаёт весь справочник; с фильтром `from`/`to`
 * активен только для реального кода типа (в режиме создания кода ещё нет).
 */
export const useKmRelationTypes = (params?: KmRelationTypesParams) => {
  const filterCode = params?.from ?? params?.to;

  return useQuery({
    queryKey: kmKeys.relationTypeList(params),
    queryFn: () => fetchKmRelationTypes(params),
    enabled: filterCode === undefined || isRealCode(filterCode),
  });
};

/**
 * Мутация изменения экземпляра: значения атрибутов и условие применимости.
 * Обновляет карточку экземпляра и инвалидирует всё, где он показан списком или графом.
 */
export const useUpdateKmEntity = () =>
  useMutation({
    mutationFn: updateKmEntity,
    onSuccess: (data) => {
      queryClient.setQueryData(kmKeys.entityDetail(data.id), data);
      queryClient.invalidateQueries({ queryKey: kmKeys.entityLists() });
      queryClient.invalidateQueries({ queryKey: kmKeys.entityInfiniteLists() });
      queryClient.invalidateQueries({ queryKey: kmKeys.entitySearches() });
      queryClient.invalidateQueries({ queryKey: kmKeys.entityGraphs() });
    },
  });

/**
 * Мутация создания типа сущности; инвалидирует список.
 */
export const useCreateKmEntityType = () =>
  useMutation({
    mutationFn: createKmEntityType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: kmKeys.entityTypeLists() });
    },
  });

/**
 * Мутация изменения типа сущности; обновляет detail-кэш и инвалидирует список.
 */
export const useUpdateKmEntityType = () =>
  useMutation({
    mutationFn: updateKmEntityType,
    onSuccess: (data, variables) => {
      queryClient.setQueryData(kmKeys.entityTypeDetail(variables.code), data);
      queryClient.invalidateQueries({ queryKey: kmKeys.entityTypeLists() });
    },
  });

/**
 * Мутация мягкого удаления типа сущности; чистит detail-кэш и инвалидирует список.
 */
export const useDeleteKmEntityType = () =>
  useMutation({
    mutationFn: deleteKmEntityType,
    onSuccess: (_data, code) => {
      queryClient.removeQueries({ queryKey: kmKeys.entityTypeDetail(code) });
      queryClient.invalidateQueries({ queryKey: kmKeys.entityTypeLists() });
    },
  });
